import { Injectable, PLATFORM_ID, Inject } from '@angular/core';
import { BehaviorSubject, map, Observable, distinctUntilChanged } from 'rxjs';
import { isPlatformBrowser } from '@angular/common';
import { environment } from '../../../environments/environment';
import { FindQuote } from '../models/leasing/FindQuote';
import { QuoteProperty } from '../models/leasing/QuoteProperty';
import { AppState } from '../models/AppState';
import { ViewType, VALID_VIEWS } from '../models/Types';
import { QuoteContact } from '../models/leasing/QuoteContacts';
import { DocumentUploaded } from '../models/leasing/DocumentsUploadedByRole';
import { DocumentRequiredRole } from '../models/leasing/FindDocumentsRequiredByTypePersonByRole';
import { TaskItem, RoleTasksMap } from '../models/TaskItem';
import { TenantQuoteData, TenantQuoteObject } from '../models/leasing/TenantQuoteData';
import { LeasingForm } from '../models/LeasingForm';
import { ActivityTypeData } from '../models/leasing/ActivityTypeData';
import { ValidateForm } from '../models/leasing/ValidateForm';

type StorageValue = ViewType | number | null;

@Injectable({
  providedIn: 'root'
})
export class DataService {
  public readonly STORAGE_KEYS = {
    VIEW: 'app_vw',
    QUOTATION: 'app_qt',
    LOGIN_ROLE: 'app_role',
    IS_LINKED: 'linked',
    RADICATION_MAIN_ROL: 'rmr'
  } as const;

  private readonly encoder = new TextEncoder();
  private readonly decoder = new TextDecoder();

  private state = new BehaviorSubject<{
    view: ViewType | null;
    loginRole: number | null;
    radicationMainRole: number | null;
    quotationNumber: number | null;
    role: number | null;
    isLinked: number,
    quoteData: FindQuote | null;
    propertyQuoteData: QuoteProperty | null;
    contactQuoteData: QuoteContact[] | null;
    leasingFormData : LeasingForm[] | null;
    validateFormData: ValidateForm[] | null;
    tenantQuoteData: TenantQuoteData;
    documentsUploadedByRoleData: { [key: number]: DocumentUploaded[] };
    documentsRequiredByRoleData: { [key: number]: DocumentRequiredRole[] };
    isAuthenticated: boolean,
    homeLoaded: boolean,
    bannerClosed: boolean,
    bannerAnimated: boolean,
    idSignatureProcess: number | null,
    activityTypeData: ActivityTypeData[]
  }>({
    view: null,
    loginRole: null,
    radicationMainRole: null,
    quotationNumber: null,
    role: null,
    isLinked: 0,
    quoteData: null,
    propertyQuoteData: null,
    contactQuoteData: null,
    leasingFormData: null,
    validateFormData: null,
    tenantQuoteData: {},
    documentsUploadedByRoleData: {},
    documentsRequiredByRoleData: {},
    isAuthenticated: false,
    homeLoaded: false,
    bannerClosed: false,
    bannerAnimated: false,
    idSignatureProcess: null,
    activityTypeData: []
  });

  private isInitialized = new BehaviorSubject<boolean>(false);

  constructor(@Inject(PLATFORM_ID) private platformId: Object) {
    if (this.isBrowser) {
      this.initializeFromStorage();
    }
  }

  get currentView$(): Observable<AppState['currentView']> {
    return this.state.asObservable().pipe(
      map(state => state.view),
      distinctUntilChanged() // Solo emite si el valor cambia
      //   distinctUntilChanged((prev, curr) => prev.view === curr.view)
      // ).pipe(
      //   map(state => state.view)
    );
  }

  get quotationNumber$(): Observable<number | null> {
    return new Observable(subscriber =>
      this.state.subscribe(state => subscriber.next(state.quotationNumber)));
  }

  get loginRole$(): Observable<number | null> {
    return new Observable(subscriber =>
      this.state.subscribe(state => subscriber.next(state.loginRole)));
  }

  get quoteData$(): Observable<FindQuote | null> {
    return new Observable(subscriber =>
      this.state.subscribe(state => subscriber.next(state.quoteData)));
  }

  get propertyQuoteData$(): Observable<QuoteProperty | null> {
    return new Observable(subscriber =>
      this.state.subscribe(state => subscriber.next(state.propertyQuoteData)));
  }

  get documentsUploadedByRoleData$(): Observable<{ [key: number]: DocumentUploaded[] }> {
    return this.state.asObservable().pipe(
      map(state => state.documentsUploadedByRoleData),
      distinctUntilChanged()
    );
  }

  get documentsRequiredByRoleData$(): Observable<{ [key: number]: DocumentRequiredRole[] }> {
    return this.state.asObservable().pipe(
      map(state => state.documentsRequiredByRoleData),
      distinctUntilChanged()
    );
  }

  get isInitialized$(): Observable<boolean> {
    return this.isInitialized.asObservable();
  }

  get isAuthenticated$(): Observable<boolean> {
    return this.state.asObservable().pipe(
      map(state => state.isAuthenticated),
      distinctUntilChanged()
    );
  }

  get homeLoaded$(): Observable<boolean> {
    return this.state.asObservable().pipe(
      map(state => state.homeLoaded),
      distinctUntilChanged()
    );
  }

  async changeView(view: ViewType | null, role: number | null = null): Promise<void> {
    const currentState = this.state.getValue();
    if (currentState.view === view && currentState.role === role) {
      return;
    }

    //const isAuthenticated = view !== 'login' && currentState.quotationNumber !== null;
    const isAuthenticated = view === 'home' && !currentState.isAuthenticated;

    if (role !== null) {
      this.updateState({ role, view, isAuthenticated });
    } else {
      this.updateState({ view, isAuthenticated });
    }

    if (this.isBrowser && view !== null) {
      await this.saveToStorage(this.STORAGE_KEYS.VIEW, view);
    }
  }

  async setQuotationNumber(number: number): Promise<void> {
    this.updateState({ quotationNumber: number });
    if (this.isBrowser) {
      await this.saveToStorage(this.STORAGE_KEYS.QUOTATION, number);
    }
  }

  
  async setLoginRole(loginRole: number): Promise<void> {
    this.updateState({ loginRole: loginRole });
    if (this.isBrowser) {
      await this.saveToStorage(this.STORAGE_KEYS.LOGIN_ROLE, loginRole);
    }
  }

  async setLinked(linked: number): Promise<void> {
    this.updateState({ isLinked: linked });
    if (this.isBrowser) {
      await this.saveToStorage(this.STORAGE_KEYS.IS_LINKED, linked);
    }
  }

  updateValidateFormData(data: ValidateForm[] | null): void {
    this.updateState({ validateFormData: data });
  }

  getValidateFormData(): ValidateForm[] | null {
    return this.state.getValue().validateFormData;
  }
  

  async setRadicationMainRole(rol: number): Promise<void> {
    this.updateState({ radicationMainRole: rol });
    if (this.isBrowser) {
      await this.saveToStorage(this.STORAGE_KEYS.RADICATION_MAIN_ROL, rol);
    }
  }

  setInitialized(value: boolean): void {
    this.isInitialized.next(value);
  }

  updateFindQuoteData(data: FindQuote | null): void {
    this.updateState({ quoteData: data });
  }

  updateQuotePropertyData(data: QuoteProperty | null): void {
    this.updateState({ propertyQuoteData: data });
  }

  updateQuoteContactData(data: QuoteContact[] | null): void {
    this.updateState({ contactQuoteData: data });
  }

  updateLeasingFormData(data: LeasingForm[] | null): void {
    this.updateState({ leasingFormData: data });
  }

  updateDocumentsUploadedByRoleData(idRole: number, data: DocumentUploaded[]): void {
    const currentState = this.state.getValue();
    const updatedDocuments = {
      ...currentState.documentsUploadedByRoleData,
      [idRole]: data
    };
    this.updateState({ documentsUploadedByRoleData: updatedDocuments });
  }

  updateDocumentsRequiredByRoleData(idRole: number, data: DocumentRequiredRole[]): void {
    const currentState = this.state.getValue();
    const updatedDocuments = {
      ...currentState.documentsRequiredByRoleData,
      [idRole]: data
    };
    this.updateState({ documentsRequiredByRoleData: updatedDocuments });
  }

  getCurrentView(): AppState['currentView'] {
    return this.state.getValue().view;
  }

  updateActivityTypeData(data: ActivityTypeData): void {
    const currentState = this.state.getValue();
    const existingIndex = currentState.activityTypeData.findIndex(item => item.roleId === data.roleId);
  
    let updatedData = [...currentState.activityTypeData];
    if (existingIndex >= 0) {
      updatedData[existingIndex] = data;
    } else {
      updatedData.push(data);
    }
  
    this.updateState({ activityTypeData: updatedData });
  }
  
  getActivityTypeDataByRole(roleId: number): number {
    return this.state.getValue().activityTypeData.find(item => item.roleId === roleId)?.activityType!;
  }
  
  getCurrentRole(): number | null {
    return this.state.getValue().role;
  }

  getLoginRole(): number | null {
    return this.state.getValue().loginRole;
  }

  getLinked(): number | null {
    return this.state.getValue().isLinked;
  }
  
  getQuotationNumber(): number | null {
    return this.state.getValue().quotationNumber;
  }

  getRadicationMainRole(): number | null {
    return this.state.getValue().radicationMainRole;
  }

  getQuoteData(): FindQuote | null {
    return this.state.getValue().quoteData;
  }

  getQuotePropertyData(): QuoteProperty | null {
    return this.state.getValue().propertyQuoteData;
  }

  getQuoteContactData(): QuoteContact[] | null {
    return this.state.getValue().contactQuoteData;
  }

  getLeasingFormData(): LeasingForm[] | null {
    return this.state.getValue().leasingFormData;
  }

  getDocumentsUploadedByRoleData(idRole?: number): DocumentUploaded[] | { [key: number]: DocumentUploaded[] } {
    const data = this.state.getValue().documentsUploadedByRoleData;
    return idRole ? data[idRole] : data;
  }

  getDocumentsRequiredByRoleData(idRole?: number): DocumentRequiredRole[] | { [key: number]: DocumentRequiredRole[] } {
    const data = this.state.getValue().documentsRequiredByRoleData;
    return idRole ? data[idRole] : data;
  }

  getIsAuthenticated(): boolean {
    return this.state.getValue().isAuthenticated;
  }

  setHomeLoaded(value: boolean): void {
    this.updateState({ homeLoaded: value });
  }

  getHomeLoaded(): boolean {
    return this.state.getValue().homeLoaded;
  }

  setIdSignatureProcess(value: number): void {
    this.updateState({ idSignatureProcess: value });
  }

  getIdSignatureProcess(): number | null {
    return this.state.getValue().idSignatureProcess;
  }

  hasActiveSession(): boolean {
    return this.getQuotationNumber() !== null &&
      this.getCurrentView() !== 'login' &&
      sessionStorage.getItem(this.STORAGE_KEYS.VIEW) !== null &&
      sessionStorage.getItem(this.STORAGE_KEYS.QUOTATION) !== null;
  }

  clearAllData(): void {
    this.state.next({
      view: 'login',
      loginRole: null,
      radicationMainRole: null,
      role: null,
      quotationNumber: null,
      isLinked: 0,
      quoteData: null,
      propertyQuoteData: null,
      contactQuoteData: null,
      leasingFormData: null,
      validateFormData: null,
      tenantQuoteData: {},
      documentsRequiredByRoleData: {},
      documentsUploadedByRoleData: {},
      isAuthenticated: false,
      homeLoaded: false,
      bannerClosed: false,
      bannerAnimated: false,
      idSignatureProcess: null,
      activityTypeData: []
    });
    this.clearStorage();
  }

  private get isBrowser(): boolean {
    return isPlatformBrowser(this.platformId);
  }

  private updateState(partialState: Partial<ReturnType<typeof this.state.getValue>>): void {
    this.state.next({
      ...this.state.getValue(),
      ...partialState
    });
  }

  private async initializeFromStorage(): Promise<void> {
    const view = sessionStorage.getItem(this.STORAGE_KEYS.VIEW);
    const quotation = sessionStorage.getItem(this.STORAGE_KEYS.QUOTATION);
    const loginRole = sessionStorage.getItem(this.STORAGE_KEYS.LOGIN_ROLE);
    const isLinked = sessionStorage.getItem(this.STORAGE_KEYS.IS_LINKED);
    const radicationMainRole = sessionStorage.getItem(this.STORAGE_KEYS.RADICATION_MAIN_ROL);

    if (!view || !quotation || !loginRole || !isLinked || !radicationMainRole) {
      await this.handleInvalidState();
      return;
    }

    try {
      const decryptedView = await this.decrypt(view) as AppState['currentView'];
      const decryptedQuotation = Number(await this.decrypt(quotation));
      const decryptedLoginRole = loginRole ? Number(await this.decrypt(loginRole)) : null;
      const decryptedIsLinked = isLinked ? Number(await this.decrypt(isLinked)) : null;
      const decryptedRadicationMainRole = radicationMainRole ? Number(await this.decrypt(radicationMainRole)) : null;

      if (!this.isValidView(decryptedView) || isNaN(decryptedQuotation)) {
        await this.handleInvalidState();
        return;
      }

      const shouldRedirectToHome = decryptedView !== 'login' && decryptedQuotation !== null;

      this.updateState({
        view: shouldRedirectToHome ? 'home' : decryptedView,
        quotationNumber: decryptedQuotation,
        loginRole: decryptedLoginRole,
        isLinked: decryptedIsLinked!,
        radicationMainRole: decryptedRadicationMainRole,
        isAuthenticated: shouldRedirectToHome
      });
    } catch {
      await this.handleInvalidState();
    }
  }

  private async handleInvalidState(): Promise<void> {
    this.clearStorage();
    this.updateState({
      view: 'login',
      isAuthenticated: false
    });
    await this.saveToStorage(this.STORAGE_KEYS.VIEW, 'login');
  }

  private isValidView(view: string | null): view is ViewType {
    return view !== null && VALID_VIEWS.includes(view as ViewType);
  }

  private async saveToStorage(key: string, value: StorageValue): Promise<void> {
    if (value === null) return;
    try {
      const encrypted = await this.encrypt(value.toString());
      sessionStorage.setItem(key, encrypted);
    } catch {
      sessionStorage.removeItem(key);
    }
  }

  private clearStorage(): void {
    if (this.isBrowser) {
      Object.values(this.STORAGE_KEYS).forEach(key => sessionStorage.removeItem(key));
    }
  }

  async encrypt(text: string): Promise<string> {
    const key = await this.getKey();
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const encryptedData = await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv },
      key,
      this.encoder.encode(text)
    );

    const combined = new Uint8Array(iv.length + encryptedData.byteLength);
    combined.set(iv);
    combined.set(new Uint8Array(encryptedData), iv.length);

    return btoa(String.fromCharCode(...combined));
  }

  private async decrypt(encryptedText: string): Promise<string> {
    const key = await this.getKey();
    const combined = Uint8Array.from(atob(encryptedText), c => c.charCodeAt(0));

    const iv = combined.slice(0, 12);
    const encrypted = combined.slice(12);

    const decrypted = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv },
      key,
      encrypted
    );

    return this.decoder.decode(decrypted);
  }

  private async getKey(): Promise<CryptoKey> {
    const keyMaterial = await crypto.subtle.importKey(
      'raw',
      this.encoder.encode(environment.KEY),
      { name: 'PBKDF2' },
      false,
      ['deriveKey']
    );

    return crypto.subtle.deriveKey(
      {
        name: 'PBKDF2',
        salt: this.encoder.encode('fixed-salt'),
        iterations: 100000,
        hash: 'SHA-256'
      },
      keyMaterial,
      { name: 'AES-GCM', length: 256 },
      false,
      ['encrypt', 'decrypt']
    );
  }

  private buildTasksForRole(role: number): TaskItem[] {
    const tasks: TaskItem[] = [];
    const validateForms = this.getValidateFormData();
    const contacts = this.getQuoteContactData();
    const applicationForms = this.getLeasingFormData();
    const hasContactInfo = contacts?.some(contact => contact.inIdRole === role) ?? false;
    const hasApplicationFormInfo = applicationForms?.some(app => app.idRol == role && app.status == true) ?? false;
    const sendedSignio = applicationForms?.some(app => app.idRol == role && app.sended == true) ?? false;

    const form = validateForms?.find(f => f.idRole === role);
    const hasApplicationFormInfoNEW = form?.stateValidateForms === true || false;
    const sendedSignioNEW = form?.isEmailSent === true || false;


    // Agregar tarea base de información básica
    tasks.push({
      id: -1,
      name: 'Información básica',
      type: 'operational',
      required: true,
      icon: 'fa-file-alt',
      completed: hasContactInfo,
      sended: false,
      order: 0
    });

    if (hasContactInfo && role !== 2) {
      tasks.push({
        id: -2,
        name: 'Formulario Solicitud',
        type: 'application-form',
        required: true,
        icon: 'fa-file-alt',
        completed: hasApplicationFormInfoNEW,
        sended: sendedSignioNEW,
        order: 1
      });
    }

    // Agregar tareas documentales
    const docTasks = this.getDocumentsRequiredByRoleData(role) as DocumentRequiredRole[];
    if (docTasks && docTasks.length > 0) {
      const uploadedDocs = this.getDocumentsUploadedByRoleData(role) as DocumentUploaded[];

      docTasks.forEach(docTask => {
        tasks.push({
          id: docTask.inIdDocumentType,
          name: docTask.strDocumentTypeName,
          type: 'document',
          description: docTask.strDocumentTypeDescription,
          required: docTask.btRequired,
          icon: 'fa-paperclip',
          completed: uploadedDocs?.some(doc => doc.inIdDocumentType === docTask.inIdDocumentType) ?? false,
          sended: false,
          order: docTask.inOrderDocument
        });
      });
    }

    // Ordenar primero por requerido (required primero) y luego por orden
    return tasks.sort((a, b) => {
      if (a.required && !b.required) return -1;
      if (!a.required && b.required) return 1;
      return a.order - b.order;
    });
  }

  getRoleTasksMap(): RoleTasksMap {
    const tasksMap: RoleTasksMap = {};
    const quoteData = this.getQuoteData();

    // Crear un conjunto de roles que necesitamos manejar
    const roles = new Set<number>();

    // Siempre incluir roles principales
    roles.add(1); // Arrendatario
    roles.add(2); // Propietario

    // Agregar roles de codeudores si existen
    if (quoteData?.inNumeroCodeudores) {
      for (let i = 0; i < quoteData.inNumeroCodeudores; i++) {
        roles.add(i + 3);
      }
    }

    // Construir tareas para cada rol
    roles.forEach(role => {
      tasksMap[role] = this.buildTasksForRole(role);
    });

    return tasksMap;
  }

  async getFromStorage(key: string): Promise<string | number | null> {
    if (!this.isBrowser) return null;

    const storedValue = sessionStorage.getItem(key);
    if (!storedValue) return null;

    try {
      const decrypted = await this.decrypt(storedValue);
      return decrypted;
    } catch {
      return null;
    }
  }

  updateTenantQuoteData(roleId: number, data: TenantQuoteObject): void {
    const currentState = this.state.getValue();
    const updatedTenantData: TenantQuoteData = {
      ...currentState.tenantQuoteData,
      [roleId]: data
    };
    this.updateState({ tenantQuoteData: updatedTenantData });
  }

  getTenantQuoteData(roleId?: number): TenantQuoteData | TenantQuoteObject | null {
    const currentState = this.state.getValue();
    if (roleId !== undefined) {
      return (currentState.tenantQuoteData as TenantQuoteData)[roleId] || null;
    }
    return currentState.tenantQuoteData;
  }

  removeTenantQuoteData(roleId: number): void {
    const currentState = this.state.getValue();
    const updatedTenantData = { ...currentState.tenantQuoteData };
    delete updatedTenantData[roleId];
    this.updateState({ tenantQuoteData: updatedTenantData as TenantQuoteData });
  }

  setBannerClosed(value: boolean): void {
    this.updateState({ bannerClosed: value });
  }

  isBannerClosed(): boolean {
    return this.state.getValue().bannerClosed;
  }

  setBannerAnimated(value: boolean): void {
    this.updateState({ bannerAnimated: value });
  }
  
  isBannerAnimated(): boolean {
    return this.state.getValue().bannerAnimated;
  }

}